import React, { useEffect, useState } from 'react';

import {
    ContractDeal,
    DeactivateEntitiesInput,
    Entity,
    EntityActiveInterval,
    EntityVendorCodes,
    GetContractDealsByIdsInput,
    GetContractDealsByIdsOutput,
    GetEntitiesInput,
    GetEntitiesOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import closeCircleTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import infoTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useHistory, useParams } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { Agreements, AgreementsTable } from 'src/components/Agreements';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { Toggle } from 'src/components/Toggle';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { CM_UI_SUPPORT_SERVICE_ID_LIST } from 'src/constants/context';
import { CAM_ROLE_SET, EDITOR_ROLE_SET } from 'src/constants/role';
import { WRITER_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IBreadcrumb } from 'src/types/IBreadcrumb';
import { IDealsRowData } from 'src/types/IDeals';
import { IToast } from 'src/types/IToaster';
import { IVendorDetailsParams, IVendorRowData } from 'src/types/IVendor';
import { getStage } from 'src/util/config';

import { BaseAgGrid } from '../BaseAgGrid';
import { BreadcrumbWrapper } from '../Breadcrumb';
import { VendorContacts } from './VendorContacts/VendorContacts';
import { VendorOverview } from './VendorOverview';
import { dealsColDefs } from './dealsGridColDefs';
import { vendorCodesColDefs } from './vendorCodesGridColDefs';

export const VendorDetails = () => {
    const [vendor, setVendor] = useState<Entity>({});
    const [deals, setDeals] = useState<IDealsRowData[]>([]);
    const [vendorCodes, setVendorCodes] = useState<IVendorRowData[]>([]);
    const [isCancelVendorModalOpen, setIsCancelVendorModalOpen] = useState(false);
    const [currentTab, setCurrentTab] = useState('overview');
    const [vendorDataLoaded, setVendorDataLoaded] = useState(false);
    const [dealsDataLoaded, setDealsDataLoaded] = useState(false);

    const dataLoaded = vendorDataLoaded && dealsDataLoaded;

    const history = useHistory();
    const params: IVendorDetailsParams = useParams();
    const vendorId = parseInt(params.vendorId);

    const { metadata, setTopAlertMsg, userInfo, userRole, setToasts } = React.useContext(AppContext);
    /*
     * Cancel Vendor button is disabled when:
     * 1. User doesn't have Admin permission OR
     * 2. Vendor has one or more Active / Ended / Upcoming deals
     * */
    const cancelVendorButtonIsDisabled = !WRITER_ROLE_SET.has(userRole) || deals.length !== 0;

    const breadcrumbItems: IBreadcrumb[] = [
        { link: ROUTES.VENDORS.INDEX, title: 'Vendors' },
        { link: '', title: 'Vendor details ' }
    ];

    useEffect(() => {
        contractConfigurationClient
            .getEntitiesByIds(
                GetEntitiesInput.clone({
                    entityIds: [vendorId],
                    serviceIds: CM_UI_SUPPORT_SERVICE_ID_LIST,
                    includeIneligibleVendorCodes: true
                })
            )
            .subscribe(
                (response: GetEntitiesOutput) => {
                    if (response.entities && response.entities.length > 0) {
                        const vendor: Entity = response.entities[0];
                        setVendor(vendor);

                        const vendorCodes = vendor.entityVendorCodes?.map((vendorCode: EntityVendorCodes) => {
                            return {
                                serviceName: vendorCode.service
                                    ? metadata.serviceMapping[vendorCode.service].serviceName
                                    : '',
                                territoryName: vendorCode.territory
                                    ? metadata.territoryMapping[vendorCode.territory].territoryCode
                                    : '',
                                ...vendorCode
                            } as IVendorRowData;
                        });
                        if (vendorCodes) setVendorCodes(vendorCodes);
                    } else {
                        const toast: IToast = {
                            id: 'vendor-not-found-toast',
                            timeout: DEFAULT_TOASTER_TIMEOUT,
                            message: 'Vendor does not exist',
                            type: 'error'
                        };
                        setToasts([toast]);
                        history.push(ROUTES.VENDORS.INDEX);
                    }
                    setVendorDataLoaded(true);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getEntitiesByIds Unexpected error', error.receivedException.message);
                    const toast: IToast = {
                        id: 'vendor-not-found-toast',
                        timeout: DEFAULT_TOASTER_TIMEOUT,
                        message: error.receivedException.message,
                        type: 'error'
                    };
                    setToasts([toast]);
                    history.push(ROUTES.VENDORS.INDEX);
                }
            );
    }, []);

    useEffect(() => {
        contractConfigurationClient
            .getContractDealsByIds(
                GetContractDealsByIdsInput.clone({
                    entityIds: [vendorId],
                    includeIneligibleContractDeals: true
                })
            )
            .subscribe(
                (response: GetContractDealsByIdsOutput) => {
                    const deals = response.contractDeals?.reduce((accu: IDealsRowData[], curr: ContractDeal) => {
                        const { ...rateCardInfo } = curr;
                        const dealsArrPerRateCard = curr.entityActiveIntervals?.map(
                            (entityActiveInterval: EntityActiveInterval) => {
                                return { ...rateCardInfo, ...entityActiveInterval } as IDealsRowData;
                            }
                        );
                        if (dealsArrPerRateCard) accu.push(...dealsArrPerRateCard);
                        return accu;
                    }, [] as IDealsRowData[]);
                    setDeals(deals ? deals : []);
                    setDealsDataLoaded(true);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getContractDealsByIds Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    }, []);

    const handleCancelVendor = () => {
        contractConfigurationClient
            .deactivateEntities(
                DeactivateEntitiesInput.clone({ changedBy: userInfo.username, entityIds: [Number(vendorId)] })
            )
            .subscribe(
                () => {
                    const toast: IToast = {
                        id: 'cancel-vendor-toast',
                        timeout: DEFAULT_TOASTER_TIMEOUT,
                        message: 'Vendor has been cancelled'
                    };
                    setToasts([toast]);
                    history.push(ROUTES.VENDORS.INDEX);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('deactivateEntities Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    };

    return (
        <Column width="100%" height="100%" spacing="200">
            <BreadcrumbWrapper breadcrumbItems={breadcrumbItems} />
            <Column spacing="200">
                <Row alignmentHorizontal="justify">
                    <Heading level={1}>{dataLoaded ? vendor.entityName || '' : <Skeleton width={300} />}</Heading>
                    <Row>
                        <Button
                            type="link"
                            size="small"
                            href={`${ROUTES.VENDORS.INDEX}${ROUTES.VENDORS.EDIT}/${vendorId}`}
                            onClick={history.push}
                            disabled={!EDITOR_ROLE_SET.has(userRole) || !dataLoaded}
                        >
                            <Icon tokens={editTokens} />
                            Edit
                        </Button>
                        <Row spacing="none">
                            <Button
                                type="link"
                                size="small"
                                onClick={() => setIsCancelVendorModalOpen(true)}
                                disabled={cancelVendorButtonIsDisabled || !dataLoaded}
                                data-testid="cancel-vendor-card-btn"
                            >
                                <Icon className="button-icon" tokens={closeCircleTokens} />
                                Cancel vendor
                            </Button>
                            {cancelVendorButtonIsDisabled && (
                                <Tooltip
                                    position="bottom"
                                    title="To cancel a vendor you mush have admin permissions AND the vendor must have no active/ended/upcoming deals"
                                >
                                    <Button className="info-button" type="link" size="small">
                                        <Icon className="info-button-icon" tokens={infoTokens} />
                                    </Button>
                                </Tooltip>
                            )}
                        </Row>
                    </Row>
                </Row>
                <Text type="b100">ID: {dataLoaded ? vendor.entityId : <Skeleton width={282} />}</Text>
                <Text>Description: {dataLoaded ? vendor.entityNotes : <Skeleton width={209} />}</Text>
            </Column>
            <Row
                height="100%"
                alignmentVertical="stretch"
                wrap="down"
                widths={['fill']}
                spacingInset="400 none none none"
            >
                <Column height="100%" heights={['fit', 'fill']}>
                    <TabGroup value={currentTab} onChange={setCurrentTab} fill="line">
                        <Tab value="overview">Overview</Tab>
                        {getStage() === 'personal' && (
                            <Tab value="contacts">
                                Contacts{' '}
                                <Tooltip title="This section uses mock data" position="end">
                                    <Icon tokens={infoTokens} />
                                </Tooltip>
                            </Tab>
                        )}
                        <Tab value="deals">Deals</Tab>
                        <Tab value="vendor-codes">Vendor Codes</Tab>
                        <Tab value="settings">Settings</Tab>
                        <Tab value="agreements">Agreements</Tab>
                    </TabGroup>
                    <Column heights="fill">
                        {currentTab === 'overview' && (
                            <VendorOverview
                                vendor={vendor}
                                vendorCodes={vendorCodes}
                                deals={deals}
                                dataLoaded={dataLoaded}
                            />
                        )}
                        {currentTab === 'contacts' && <VendorContacts />}
                        {currentTab === 'agreements' && <Agreements licensors={[vendor.entityName ?? '']} />}
                        {currentTab === 'deals' && <BaseAgGrid columnDefs={dealsColDefs} rowData={deals} />}
                        {currentTab === 'vendor-codes' && (
                            <BaseAgGrid columnDefs={vendorCodesColDefs} rowData={vendorCodes} />
                        )}
                        {currentTab === 'settings' && (
                            <Column height="100%" heights="fit">
                                <Box type="outline" spacingInset="400">
                                    <Column spacing="300">
                                        <Heading level={4}>Submission Settings</Heading>
                                        <Text type="b200" color="secondary">
                                            Enable or disable the accounting and payments for this vendor
                                        </Text>
                                        <Column>
                                            <Toggle
                                                label="Submit Accruals"
                                                value={vendor.submitAccruals!}
                                                isEditMode={false}
                                            />
                                            <Toggle
                                                label="Submit Payments"
                                                value={vendor.submitPayments!}
                                                isEditMode={false}
                                            />
                                        </Column>
                                    </Column>
                                </Box>
                            </Column>
                        )}
                    </Column>
                </Column>
            </Row>
            <ConfirmationModal
                isOpen={isCancelVendorModalOpen}
                onClose={() => setIsCancelVendorModalOpen(false)}
                title="Cancel vendor"
                body="Are you sure you want to cancel this vendor?"
                primaryButtonText="Yes, cancel vendor"
                primaryButtonOnClick={handleCancelVendor}
                secondaryButtonText="No"
                secondaryButtonOnClick={() => setIsCancelVendorModalOpen(false)}
            />
        </Column>
    );
};
